import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import { Card, Button, Slider, Typography, Dropdown, Image, Spin, Select } from "antd";
import { PauseCircleFilled, CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { isNaN, isNumber, isString, set } from "lodash";
import { formatDateTime } from "../../utils/dateTimes";
import PlayIcon from "../../icons/svg/play-icon.svg";
import MoreIcon from "../../icons/svg/more.svg";
import SpeakerIcon from "../../icons/svg/speaker.svg";
import MuteSpeakerIcon from "../../icons/svg/mute-speaker.svg";
import FastForwardIcon from "../../icons/svg/fast-forward.svg";
import RewindIcon from "../../icons/svg/rewind.svg";

import { audioDownloader } from "../../utils/mediaDownloader";

const { Title } = Typography;

/**
 * CallAudioPlayer Component
 *
 * A component that renders an audio player specifically for call recordings.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.visible - Whether the audio player is visible
 * @param {string} props.title - Title of the audio player
 * @param {string} props.audioSrc - URL of the audio source
 * @param {string} props.duration - Duration of the audio in "mm:ss" format
 * @param {string} props.timestamp - Timestamp of when the call was recorded
 * @param {Function} props.onClose - Callback function when the audio player is closed
 * @returns {React.ReactElement} The CallAudioPlayer component
 */
const CallAudioPlayer = forwardRef(
  (
    {
      visible = false,
      title,
      audioSrc,
      duration,
      timestamp,
      onClose,
      hideHeader = false,
      hideExtra = false,
      hideTimestamp = false,
      bordered = true,
    },
    ref
  ) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [progress, setProgress] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [audioDuration, setAudioDuration] = useState(0);

    const audioRef = useRef(null);
    const intervalRef = useRef(null);

    /**
     * Stop and reset audio completely
     */
    const stopAndResetAudio = () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
      clearInterval(intervalRef.current);
    };

    // Expose methods to parent component
    useImperativeHandle(
      ref,
      () => ({
        stopAudio: () => {
          stopAndResetAudio();
        },
        pauseAudio: () => {
          if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
          }
        },
        playAudio: () => {
          if (audioRef.current) {
            audioRef.current.play();
            setIsPlaying(true);
          }
        },
      }),
      []
    );

    // Parse duration string in "mm:ss" format to seconds
    const parseDuration = durationStr => {
      if (!durationStr) return 0;

      // Handle different duration formats
      if (isNumber(durationStr)) {
        return durationStr;
      }

      if (isString(durationStr)) {
        // Handle "Xm Ys" format (e.g., "5m 30s")
        if (durationStr.includes("m") && durationStr.includes("s")) {
          const matches = durationStr.match(/(\d+)m\s*(\d+)s/);
          if (matches) {
            return parseInt(matches[1]) * 60 + parseInt(matches[2]);
          }
        }

        // Handle "mm:ss" format
        if (durationStr.includes(":")) {
          const parts = durationStr.split(":");
          if (parts.length === 2) {
            const minutes = parseInt(parts[0]) || 0;
            const seconds = parseInt(parts[1]) || 0;
            return minutes * 60 + seconds;
          }
        }

        // Handle just seconds as string
        const numValue = parseInt(durationStr);
        if (!isNaN(numValue)) {
          return numValue;
        }
      }

      return 0;
    };

    const totalDurationInSeconds = parseDuration(duration);

    /**
     * Handles rewinding the audio by 10 seconds
     */
    const handleRewind = () => {
      if (audioRef.current) {
        const { currentTime } = audioRef.current;

        const newTime = Math.max(0, currentTime - 10);

        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);

        // Use actual audio duration if available, otherwise use parsed duration
        const durationToUse = audioDuration || totalDurationInSeconds;
        if (durationToUse > 0) {
          setProgress((newTime / durationToUse) * 100);
        }
      }
    };

    /**
     * Handles fast forwarding the audio by 10 seconds
     */
    const handleFastForward = () => {
      if (audioRef.current) {
        const { currentTime } = audioRef.current;
        // Use actual audio duration if available, otherwise use parsed duration
        const durationToUse = audioDuration || totalDurationInSeconds;

        const newTime = Math.min(durationToUse, currentTime + 10);

        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);

        if (durationToUse > 0) {
          setProgress((newTime / durationToUse) * 100);
        }
      }
    };

    /**
     * Handles playback speed change
     */
    const handleSpeedChange = speed => {
      if (audioRef.current) {
        audioRef.current.playbackRate = speed;
        setPlaybackSpeed(speed);
      }
    };

    /**
     * Toggles the mute state of the audio player
     */
    const handleToggleMute = () => {
      const audio = audioRef.current;

      if (audio) {
        // Direct control of <audio> element
        audio.muted = !audio.muted;
        setIsMuted(audio.muted);
      } else {
        // For external APIs (WebRTC, Twilio), use this toggle
        setIsMuted(prev => !prev);
        // Example: externalStream?.getAudioTracks()[0].enabled = !isMuted;
      }
    };

    const formatTime = timeInSeconds => {
      const minutes = Math.floor(timeInSeconds / 60);
      const seconds = Math.floor(timeInSeconds % 60);
      return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
    };

    /**
     * Handles the download of the audio file
     */
    const handleDownloadAudio = async () => {
      try {
        setIsDownloading(true);
        const fileName = title ? `call-with-${title}` : "call-recording";
        await audioDownloader(audioSrc, fileName, timestamp);
      } catch (error) {
        setIsDownloading(false);
      } finally {
        setIsDownloading(false);
      }
    };

    /**
     * Handles play/pause toggle
     */
    const handlePlayPause = () => {
      if (isPlaying) {
        audioRef.current?.pause();
      } else {
        audioRef.current?.play();
      }
      setIsPlaying(!isPlaying);
    };

    // Update audio progress
    const startTimer = () => {
      clearInterval(intervalRef.current);

      intervalRef.current = setInterval(() => {
        if (audioRef.current) {
          const current = audioRef.current.currentTime;
          setCurrentTime(current);

          // Use actual audio duration if available, otherwise use parsed duration
          const durationToUse = audioDuration || totalDurationInSeconds;
          if (durationToUse > 0) {
            setProgress((current / durationToUse) * 100);
          }

          if (current >= durationToUse) {
            setIsPlaying(false);
            clearInterval(intervalRef.current);
          }
        }
      }, 1000);
    };

    // Handle seek on slider change
    const handleSliderChange = value => {
      const durationToUse = audioDuration || totalDurationInSeconds;
      const seekTime = (value / 100) * durationToUse;
      setProgress(value);
      setCurrentTime(seekTime);
      if (audioRef.current) {
        audioRef.current.currentTime = seekTime;
      }
    };

    const moreMenuItems = [
      {
        key: "1",
        label: "Download",
        onClick: handleDownloadAudio,
      },
      // { key: "2", label: "Share" },
    ];

    const speedOptions = [
      { value: 1, label: "1.0x" },
      { value: 2, label: "2.0x" },
      { value: 4, label: "4.0x" },
    ];

    useEffect(() => {
      if (isPlaying) {
        startTimer();
      } else {
        clearInterval(intervalRef.current);
      }

      return () => {
        clearInterval(intervalRef.current);
      };
    }, [isPlaying]);

    // Stop audio when audioSrc changes or becomes null
    useEffect(() => {
      if (!audioSrc) {
        stopAndResetAudio();
      }
    }, [audioSrc]);

    // Enhanced cleanup on unmount - just clear interval since audio should already be stopped
    useEffect(() => {
      return () => {
        clearInterval(intervalRef.current);
        // Don't try to access audioRef here as it might be null
      };
    }, []);

    if (!visible) return null;

    return (
      <div className="w-full" onClick={e => e.stopPropagation()}>
        <Card
          className="w-full bg-white rounded-xl !shadow-none"
          styles={{
            body: { padding: bordered ? "16px" : 0 },
            header: {
              color: "#333333",
              padding: "16px",
              minHeight: "auto",
              borderBottom: "none",
            },
          }}
          bordered={bordered}
          extra={
            hideExtra ? null : (
              <Button
                type="text"
                icon={<CloseOutlined className="text-gray-600" />}
                onClick={e => {
                  e.stopPropagation();
                  stopAndResetAudio(); // Stop audio when close button is clicked
                  onClose && onClose(e);
                }}
                className="p-0 h-auto"
              />
            )
          }
          title={
            hideHeader ? null : (
              <div className="flex items-center">
                <Title level={5} className="m-0 text-gray-800 text-sm font-medium">
                  {title || "Call Recording"}
                </Title>
              </div>
            )
          }
        >
          <audio
            ref={audioRef}
            src={audioSrc}
            onPlay={e => {
              e.stopPropagation();
              setIsPlaying(true);
            }}
            onPause={e => {
              e.stopPropagation();
              setIsPlaying(false);
            }}
            onEnded={e => {
              e.stopPropagation();
              setIsPlaying(false);
            }}
            onLoadedMetadata={e => {
              e.stopPropagation();
              setAudioDuration(e.target.duration);
            }}
            onTimeUpdate={e => {
              e.stopPropagation();
              if (audioRef.current) {
                const current = audioRef.current.currentTime;
                setCurrentTime(current);

                const durationToUse = audioDuration || totalDurationInSeconds;
                if (durationToUse > 0) {
                  setProgress((current / durationToUse) * 100);
                }
              }
            }}
            className="hidden"
            preload="metadata"
            aria-disabled={audioSrc ? "false" : "true"}
          >
            <track kind="captions" src="" srcLang="en" label="English captions" default />
          </audio>

          <div
            className="flex bg-[#F5F5F5] p-2 rounded-full items-center"
            onClick={e => e.stopPropagation()}
          >
            <Button
              type="text"
              icon={<Image preview={false} src={FastForwardIcon} className="text-gray-600" />}
              className="flex items-center p-1"
              onClick={e => {
                e.stopPropagation();
                handleRewind();
              }}
              disabled={!audioSrc}
              title="Rewind 10 seconds"
            />

            <Button
              type="text"
              className="flex items-center justify-center p-0"
              onClick={e => {
                e.stopPropagation();
                handlePlayPause();
              }}
              disabled={!audioSrc}
              shape="circle"
              icon={
                isPlaying ? (
                  <PauseCircleFilled className="text-[#1677FF] text-xl" />
                ) : (
                  <Image preview={false} src={PlayIcon} className="text-[#1677FF] text-xl" />
                )
              }
            />

            <Button
              type="text"
              icon={<Image preview={false} src={RewindIcon} className="text-gray-600" />}
              className="flex items-center p-1"
              onClick={e => {
                e.stopPropagation();
                handleFastForward();
              }}
              disabled={!audioSrc}
              title="Fast forward 10 seconds"
            />

            <Typography.Text className="text-xs text-gray-600 min-w-[70px]">
              {formatTime(currentTime)} / {duration}
            </Typography.Text>

            <Slider
              value={progress}
              disabled={!audioSrc}
              onChange={handleSliderChange}
              className="flex-1 my-0"
              tooltip={{ formatter: null }}
              styles={{
                track: {
                  background: "#1677FF",
                  height: "3px !important",
                },
                rail: {
                  background: "#E5E7EB",
                  height: "3px !important",
                },
                handle: {
                  display: "none",
                },
              }}
            />

            <Button
              type="text"
              icon={
                <Image
                  preview={false}
                  src={isMuted ? MuteSpeakerIcon : SpeakerIcon}
                  className="text-gray-600"
                />
              }
              className="flex items-center p-1"
              onClick={e => {
                e.stopPropagation();
                handleToggleMute();
              }}
            />

            <Select
              value={playbackSpeed}
              options={speedOptions}
              onChange={value => {
                handleSpeedChange(value);
              }}
              onClick={e => e.stopPropagation()}
              size="small"
              className="min-w-[60px] !text-xs"
              popupMatchSelectWidth={false}
              getPopupContainer={trigger => trigger.parentNode}
            />

            <Dropdown
              menu={{ items: moreMenuItems }}
              placement="topRight"
              trigger={["click"]}
              getPopupContainer={trigger => trigger.parentNode}
            >
              <Button
                type="text"
                icon={<Image preview={false} src={MoreIcon} className="text-gray-600" />}
                className="flex items-center p-1"
                onClick={e => e.stopPropagation()}
              />
            </Dropdown>
            {isDownloading && <LoadingOutlined />}
          </div>

          {!hideTimestamp && (
            <Typography.Text className="text-xs text-gray-400 block text-start mt-3">
              {formatDateTime(timestamp)}
            </Typography.Text>
          )}
        </Card>
      </div>
    );
  }
);

CallAudioPlayer.displayName = "CallAudioPlayer";

export default CallAudioPlayer;
